import type { components } from '@multaqa/api-client'

/** The typed boundary to the fixed-asset endpoints under `/api/accounting`. */
export type FixedAsset = components['schemas']['FixedAssetResource']
export type FixedAssetCategory = components['schemas']['FixedAssetCategoryResource']
export type FixedAssetDepreciation = components['schemas']['FixedAssetDepreciationResource']
export type Account = components['schemas']['AccountResource']
export type FiscalPeriod = components['schemas']['FiscalPeriodResource']

/** The four lifecycle states (the backend's closed `FixedAssetStatus` enum). */
export const FIXED_ASSET_STATUSES = [
  'draft',
  'in_service',
  'fully_depreciated',
  'disposed',
] as const

export type FixedAssetStatus = (typeof FIXED_ASSET_STATUSES)[number]

/**
 * The register's URL-backed filters.
 *
 * `GET /accounting/fixed-assets` exposes no query parameters
 * (`fixedAsset.index` is `query?: never` in the generated contract), so both
 * are matched client-side over the fetched rows; the route owns the values so
 * a filtered register is linkable and survives a reload.
 */
export interface AssetFilters {
  q?: string
  status?: FixedAssetStatus
}

export type DepreciationMethod = 'straight_line' | 'declining_balance'

/** The create body, with the bilingual name corrected. */
export type FixedAssetStoreRequest = Omit<
  components['schemas']['FixedAssetStoreRequest'],
  'name'
> & { name: Record<string, string> }

/** The minimal PATCH body, with the same name correction. */
export type FixedAssetUpdateRequest = Omit<
  components['schemas']['FixedAssetUpdateRequest'],
  'name'
> & { name?: Record<string, string> | null }

export interface FiscalYearWithPeriods {
  id: string
  code: string
  starts_on: string
  ends_on: string
  status: string
  periods: FiscalPeriod[]
}

/** The run's summary — posted/skipped counts, the total as a decimal string. */
export interface DepreciationRunResult {
  posted: number
  skipped: number
  total: string
}

export type FixedAssetDisposeRequest = components['schemas']['FixedAssetDisposeRequest']
export type FixedAssetDisposal = components['schemas']['FixedAssetDisposalResource']
